import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote, Star, type LucideIcon } from 'lucide-react';

import { Badge, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui';
import { cn } from '@/shared/utils/helpers';
import type {
  FaqItem,
  MetricItem,
  ProductItem,
  ProjectItem,
  ServiceItem,
  TestimonialItem
} from '@/shared/utils/SolarData';

type SectionHeadingProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
};

export const SectionHeading = ({
  eyebrow,
  title,
  description,
  align = 'left'
}: SectionHeadingProps) => {
  return (
    <div className={cn('max-w-3xl space-y-4', align === 'center' ? 'mx-auto text-center' : 'text-left')}>
      {eyebrow ? (
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-accent-primary">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl">
        {title}
      </h2>
      {description ? (
        <p className="text-base leading-7 text-text-secondary sm:text-lg">{description}</p>
      ) : null}
    </div>
  );
};

type StatCardProps = MetricItem & { className?: string; icon?: LucideIcon };

export const StatCard = ({ value, label, description, icon: Icon, className }: StatCardProps) => {
  return (
    <Card className={cn('h-full border-border-primary/70 bg-background-card/90 shadow-xl shadow-accent-primary/5 backdrop-blur', className)}>
      <CardContent className="px-6 pb-6 pt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-3xl font-semibold text-text-primary sm:text-4xl">{value}</p>
            <p className="mt-2 text-sm font-medium uppercase tracking-[0.2em] text-text-tertiary">
              {label}
            </p>
            <p className="mt-3 text-sm leading-6 text-text-secondary">{description}</p>
          </div>
          {Icon ? (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent-primary/10 text-accent-primary">
              <Icon className="h-5 w-5" />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

type FeatureCardProps = {
  icon: LucideIcon;
  title: string;
  description: string;
  className?: string;
};

export const FeatureCard = ({ icon: Icon, title, description, className }: FeatureCardProps) => {
  return (
    <Card className={cn('group h-full overflow-hidden border-border-primary/80 bg-background-card/90', className)}>
      <CardContent className="px-6 pb-6 pt-4">
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-primary/10 text-accent-primary transition-transform group-hover:scale-105">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-semibold text-text-primary">{title}</h3>
        <p className="mt-3 text-sm leading-6 text-text-secondary">{description}</p>
      </CardContent>
    </Card>
  );
};

type ServiceCardProps = ServiceItem & { index?: number; className?: string };

export const ServiceCard = ({
  icon: Icon,
  title,
  description,
  points,
  index = 0,
  className
}: ServiceCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className={className}
    >
      <Card className="h-full border-border-primary/70 bg-background-card/90 shadow-lg shadow-accent-secondary/5 backdrop-blur transition-transform duration-300 hover:-translate-y-1">
        <CardHeader className="space-y-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-linear-to-br from-accent-warning/15 to-accent-primary/15 text-accent-primary">
            <Icon className="h-6 w-6" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription className="text-sm leading-6">{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {points.map(point => (
            <div key={point} className="flex items-start gap-3 text-sm text-text-secondary">
              <span className="mt-1 h-2 w-2 rounded-full bg-accent-warning" />
              <span>{point}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
};

type ProductCardProps = ProductItem & { index?: number; className?: string };

export const ProductCard = ({
  icon: Icon,
  title,
  description,
  useCase,
  index = 0,
  className
}: ProductCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className={className}
    >
      <Card className="h-full border-border-primary/70 bg-background-card/90 shadow-lg shadow-accent-secondary/5 backdrop-blur transition-transform duration-300 hover:-translate-y-1">
        <CardHeader className="space-y-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-linear-to-br from-accent-warning/15 to-accent-primary/15 text-accent-secondary">
            <Icon className="h-6 w-6" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription className="text-sm leading-6">{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="rounded-2xl bg-background-secondary px-4 py-3 text-sm font-medium text-text-primary">
            {useCase}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

type ProjectCardProps = ProjectItem & { index?: number; className?: string };

export const ProjectCard = ({
  title,
  location,
  capacity,
  description,
  result,
  highlight,
  index = 0,
  className
}: ProjectCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className={cn('h-full', className)}
    >
      <Card className="h-full overflow-hidden border-border-primary/70 bg-background-card/90 shadow-lg shadow-accent-primary/5 backdrop-blur">
        <div className="relative h-48 overflow-hidden bg-linear-to-br from-accent-secondary via-accent-primary to-accent-warning">
          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.15),transparent_35%)]" />
          <div className="absolute inset-0 flex items-end p-5">
            <div className="space-y-2 text-text-inverse">
              <Badge className="bg-white/15 text-text-inverse hover:bg-white/15">{highlight}</Badge>
              <p className="text-xs uppercase tracking-[0.24em] text-text-inverse/80">{location}</p>
            </div>
          </div>
        </div>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-xl font-semibold text-text-primary">{title}</h3>
            <Badge className="bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/10">
              {capacity}
            </Badge>
          </div>
          <p className="text-sm leading-6 text-text-secondary">{description}</p>
          <div className="rounded-2xl border border-border-primary/70 bg-background-secondary px-4 py-3 text-sm font-medium text-text-primary">
            Result: {result}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

type TestimonialsCarouselProps = {
  testimonials: TestimonialItem[];
};

export const TestimonialsCarousel = ({ testimonials }: TestimonialsCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % testimonials.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [testimonials.length]);

  const activeTestimonial = testimonials[activeIndex];

  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTestimonial.name}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -18 }}
          transition={{ duration: 0.35 }}
        >
          <Card className="h-full border-border-primary/70 bg-linear-to-br from-background-card to-background-secondary shadow-xl shadow-accent-primary/5">
            <CardContent className="space-y-6 p-8">
              <Quote className="h-10 w-10 text-accent-primary/30" />
              <p className="text-lg leading-8 text-text-primary sm:text-xl">{activeTestimonial.quote}</p>
              <div className="flex items-center gap-2 text-accent-warning">
                {Array.from({ length: activeTestimonial.rating }).map((_, index) => (
                  <Star key={index} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <div>
                <p className="text-lg font-semibold text-text-primary">{activeTestimonial.name}</p>
                <p className="text-sm text-text-secondary">
                  {activeTestimonial.role} - {activeTestimonial.company}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid gap-4">
        {testimonials.map((testimonial, index) => (
          <button
            key={testimonial.name}
            type="button"
            onClick={() => setActiveIndex(index)}
            className={cn(
              'rounded-3xl border p-5 text-left transition-all duration-300',
              activeIndex === index
                ? 'border-accent-primary bg-accent-primary/10 shadow-lg shadow-accent-primary/10'
                : 'border-border-primary/70 bg-background-card/80 hover:border-accent-primary/50'
            )}
          >
            <p className="text-sm font-semibold text-text-primary">{testimonial.name}</p>
            <p className="mt-1 text-sm text-text-secondary">{testimonial.role}</p>
          </button>
        ))}

        <div className="flex items-center justify-between rounded-3xl border border-border-primary/70 bg-background-secondary px-5 py-4">
          <button
            type="button"
            onClick={() =>
              setActiveIndex(current => (current - 1 + testimonials.length) % testimonials.length)
            }
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border-primary/70 bg-background-primary transition-colors hover:bg-background-hover"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex gap-2">
            {testimonials.map((testimonial, index) => (
              <button
                key={`${testimonial.name}-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={cn(
                  'h-2.5 rounded-full transition-all duration-300',
                  activeIndex === index ? 'w-8 bg-accent-primary' : 'w-2.5 bg-border-primary'
                )}
                aria-label={`Show testimonial ${index + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => setActiveIndex(current => (current + 1) % testimonials.length)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border-primary/70 bg-background-primary transition-colors hover:bg-background-hover"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

type FaqSectionProps = {
  items: FaqItem[];
  className?: string;
};

export const FaqSection = ({ items, className }: FaqSectionProps) => {
  return (
    <div className={cn('grid gap-4', className)}>
      {items.map(item => (
        <details
          key={item.question}
          className="group rounded-3xl border border-border-primary/70 bg-background-card/85 p-5 shadow-sm transition-colors open:border-accent-primary/40"
        >
          <summary className="cursor-pointer list-none text-base font-semibold text-text-primary">
            <span className="flex items-center justify-between gap-4">
              {item.question}
              <span className="text-accent-primary transition-transform group-open:rotate-45">+</span>
            </span>
          </summary>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-text-secondary">{item.answer}</p>
        </details>
      ))}
    </div>
  );
};
